import React, { useState } from 'react';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import VisibilityIcon from '@mui/icons-material/Visibility';
import moment from 'moment-timezone';
import { useLikes, useBookmarks } from '@/Recoil/Social';
import { useUsers } from '@/Recoil/User';
import { handleLinkClick } from '@/Utils/routing';
import { FastAvatar } from '@/Components/FastAvatar';
import type { Post } from '@/Data/Social';
import './styles/PostCard.scss';

interface PostCardProps {
    post: Post;
}

const PostCard: React.FC<PostCardProps> = ({ post }) => {
    const { isPostLiked, likePost, unlikePost } = useLikes();
    const { bookmarkPost, unbookmarkPost, postBookmarks } = useBookmarks();
    const { profiles, syncProfiles } = useUsers();
    const [showTranslation, setShowTranslation] = useState(false);
    
    const liked = isPostLiked(post.postid);
    const bookmarked = postBookmarks[post.postid]?.userBookmarked ?? false;
    
    // Get user profile
    const userProfile = profiles[post.addressid];
    
    // Sync user profile if not loaded
    React.useEffect(() => {
        if (!userProfile) {
            syncProfiles([post.addressid]);
        }
    }, [post.addressid, userProfile, syncProfiles]);
    
    const handleLike = async () => {
        try {
            if (liked) {
                await unlikePost(post.postid);
            } else {
                await likePost(post.postid);
            }
        } catch (error) {
            console.error('Error toggling like:', error);
        }
    };
    
    const handleBookmark = async () => {
        try {
            if (bookmarked) {
                await unbookmarkPost(post.postid);
            } else {
                await bookmarkPost(post.postid);
            }
        } catch (error) {
            console.error('Error toggling bookmark:', error);
        }
    };
    
    const formatTime = (timestamp: number) => {
        return moment(timestamp * 1000).tz('asia/seoul').format('YYYY년 MM월 DD일 HH:mm');
    };
    
    // Extract content without hashtags
    const contentWithoutHashtags = post.content.replace(/#\w+/g, '').trim();
    const hashtags = post.content.match(/#\w+/g) || [];
    const mentions = post.mentions || [];
    
    // Check if translation should have toggle
    const shouldShowTranslationToggle = post.translated && post.translated.split('\n').length > 5;
    
    return (
        <article className="PostCard">
            {/* User Header Section */}
            <div className="UserHeader">
                <div className="UserHeaderLeft">
                    <FastAvatar 
                        uri={userProfile?.image}
                        userid={post.addressid}
                        size={40}
                        style={{ borderRadius: '50%' }}
                    />
                    <div className="UserInfo">
                        <div className="DisplayName">{userProfile?.name || post.addressid}</div>
                        <div className="Username">@{post.addressid}</div>
                    </div>
                </div>
            </div>
            
            {/* Content Section - Clickable Area */}
            <div className="ContentSection">
                {/* Mentions */}
                {mentions.length > 0 && (
                    <div className="Mentions">
                        {mentions.map((mention, index) => (
                            <span key={index} className="Mention">@{typeof mention === 'string' ? mention : (mention as any).addressid || mention}</span>
                        ))}
                    </div>
                )}
                
                {/* Main Content Text (without hashtags) */}
                <div className="ContentText">
                    {contentWithoutHashtags}
                </div>
                
                {/* Translation Section */}
                {post.translated === null ? (
                    <div className="TranslationPending">
                        Translation will be ready in a few minutes
                    </div>
                ) : post.translated && (
                    <div className="TranslationSection">
                        {shouldShowTranslationToggle && (
                            <button 
                                className="TranslationToggle"
                                onClick={() => setShowTranslation(!showTranslation)}
                            >
                                {showTranslation ? 'Hide' : 'Show'} translation
                            </button>
                        )}
                        {(!shouldShowTranslationToggle || showTranslation) && (
                            <div className="TranslationText">
                                {post.translated}
                            </div>
                        )}
                    </div>
                )}
                
                {/* Hashtags */}
                {hashtags.length > 0 && (
                    <div className="Hashtags">
                        {hashtags.map((tag) => (
                            <span key={tag} className="Hashtag">{tag}</span>
                        ))}
                    </div>
                )}
                
                {/* Media */}
                {post.media && post.media.length > 0 && (
                    <div className={`Media Media${Math.min(post.media.length, 5)}`}>
                        {post.media.map((url, index) => (
                            <a 
                                key={index} 
                                className="MediaItem"
                                href={`/social/image/${post.postid}/${index}`}
                                onClick={handleLinkClick}
                            >
                                <img src={url} alt="" />
                            </a>
                        ))}
                    </div>
                )}
                
                {/* Created Date */}
                <div className="CreatedDate">
                    {formatTime(post.created_at || 0)}
                </div>
            </div>
            
            {/* Interaction Bar */}
            <div className="InteractionBar">
                <button className="InteractionButton">
                    <ChatBubbleOutlineIcon />
                    <span className="Count">{post.reply_count || 0}</span>
                </button>
                
                <button 
                    className={`InteractionButton ${liked ? 'Active' : ''}`}
                    onClick={handleLike}
                >
                    {liked ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                    <span className="Count">{post.like_count || 0}</span>
                </button>
                
                <button className="InteractionButton">
                    <VisibilityIcon />
                    <span className="Count">{post.view_count || 0}</span>
                </button>
                
                <button 
                    className={`InteractionButton ${bookmarked ? 'Active' : ''}`}
                    onClick={handleBookmark}
                >
                    {bookmarked ? <BookmarkIcon /> : <BookmarkBorderIcon />}
                </button>
            </div>
        </article>
    );
};

export default PostCard;